import React, { useState } from 'react';
import { 
  MagnifyingGlassIcon,
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  UserIcon
} from '@heroicons/react/24/outline';

// Crown Icon Component
const CrownIcon = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M5 16L3 8l2.5 2L12 4l6.5 6L21 8l-2 8H5zm2.5-7L6 8l1 4h10l1-4-1.5 1L12 7 7.5 9z"/>
  </svg>
);

// Mock lead data
const mockLeads = [
  {
    id: 1,
    name: 'Carol A Smith',
    location: 'Queens, NY',
    phone: '(347) 555-2503',
    fullName: 'Carol Anne Smith',
    age: 34,
    dob: '1989-03-15',
    currentAddress: '1245 Northern Blvd, Queens, NY 11354-2847',
    emails: ['carol.smith@email.com', 'csmith@work.com'],
    telephoneNumbers: ['(347) 555-2503', '(718) 555-0198'],
    called: 'Called: No',
    county: 'Queens',
    city: 'Queens',
    zip: '11354',
    business: 'Retail Manager'
  },
  {
    id: 2,
    name: 'Amy Smith',
    location: 'Long Island, NY',
    phone: '(631) 555-8690',
    fullName: 'Amy Michelle Smith',
    age: 28,
    dob: '1995-07-22',
    currentAddress: '892 Suffolk Ave, Long Island, NY 11701-3421',
    emails: ['amy.m.smith@email.com'],
    telephoneNumbers: ['(631) 555-8690'],
    called: 'Called: No',
    county: 'Suffolk',
    city: 'Long Island',
    zip: '11701',
    business: 'Teacher'
  },
  {
    id: 3,
    name: 'Kim Smith',
    location: 'San Diego, CA',
    phone: '(714) 555-8329',
    fullName: 'Kimberly Dawn Smith',
    age: 31,
    dob: '1992-11-08',
    currentAddress: '567 Pacific Coast Hwy, San Diego, CA 92101-5678',
    emails: ['kim.smith@email.com', 'k.d.smith@company.com'],
    telephoneNumbers: ['(714) 555-8329', '(619) 555-7412'],
    called: 'Called: Yes',
    county: 'San Diego',
    city: 'San Diego',
    zip: '92101',
    business: 'Marketing Specialist'
  },
  {
    id: 4,
    name: 'Jason Smith',
    location: 'Montgomery, CA',
    phone: '(937) 555-8954',
    fullName: 'Jason Michael Smith',
    age: 35,
    dob: '1988-12-03',
    currentAddress: '5065 Footman Dr, Dayton, CA 45407-1622',
    emails: ['jason.smith@email.com'],
    telephoneNumbers: ['(937) 555-8954'],
    called: 'Called: No',
    county: 'Montgomery',
    city: 'Dayton',
    zip: '45407',
    business: 'smith Assets'
  },
  {
    id: 5,
    name: 'Tonjua Smith',
    location: 'Los Angeles, CA',
    phone: '(662) 555-2304',
    fullName: 'Tonjua Marie Smith',
    age: 42,
    dob: '1981-05-17',
    currentAddress: '1428 Sunset Blvd, Los Angeles, CA 90028-4512',
    emails: ['tonjua.smith@email.com'],
    telephoneNumbers: ['(662) 555-2304'],
    called: 'Called: No',
    county: 'Los Angeles',
    city: 'Los Angeles',
    zip: '90028',
    business: 'Consultant'
  }
];

// Lead Search Result Component
const LeadSearchResult = ({ lead, onClick }) => {
  return (
    <div 
      onClick={() => onClick(lead)}
      className="bg-gray-700 hover:bg-gray-600 rounded-lg p-4 cursor-pointer transition-colors border border-gray-600"
    >
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-white font-semibold text-lg">{lead.name}</h3>
          <p className="text-gray-300 text-sm">{lead.location}</p>
        </div>
        <div className="text-right">
          <p className="text-gray-300 text-sm">{lead.phone}</p>
          <p className="text-gray-400 text-xs">{lead.called}</p>
        </div>
      </div>
    </div>
  );
};

// Lead Detail View Component
const LeadDetailView = ({ lead, onClose }) => {
  if (!lead) return null;

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Lead Details</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Full Name
            </label>
            <p className="text-white text-lg font-semibold">{lead.fullName}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Age
            </label>
            <p className="text-white">{lead.age}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              DOB
            </label>
            <p className="text-white">{lead.dob}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Business
            </label>
            <p className="text-white">{lead.business}</p>
          </div>
        </div>

        {/* Address Information */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Current Address
          </label>
          <p className="text-white">{lead.currentAddress}</p>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Emails
            </label>
            <div className="space-y-2">
              {lead.emails.map((email, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                  <p className="text-white">{email}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Telephone Numbers
            </label>
            <div className="space-y-2">
              {lead.telephoneNumbers.map((phone, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <PhoneIcon className="h-4 w-4 text-gray-400" />
                  <p className="text-white">{phone}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Location Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              County
            </label>
            <p className="text-white">{lead.county}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              City
            </label>
            <p className="text-white">{lead.city}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Zip
            </label>
            <p className="text-white">{lead.zip}</p>
          </div>
        </div>

        {/* Call Status */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Call Status
          </label>
          <p className={`font-semibold ${lead.called.includes('Yes') ? 'text-green-400' : 'text-red-400'}`}>
            {lead.called}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4 border-t border-gray-700">
          <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2">
            <PhoneIcon className="h-5 w-5" />
            <span>Call</span>
          </button>
          <button className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2">
            <EnvelopeIcon className="h-5 w-5" />
            <span>Email</span>
          </button>
          <button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors">
            Add Note
          </button>
        </div>
      </div>
    </div>
  );
};

import AdminLayoutWrapper from './common/AdminLayoutWrapper';
// Main Content Area
const MainContent = ({ children }) => {
  return (
    <AdminLayoutWrapper>
        {children}
    </AdminLayoutWrapper>
  );
};
// Main Manual CRM Component
const ManualCRMComponent = () => {
  const [searchTerm, setSearchTerm] = useState('Smith');
  const [searchResults, setSearchResults] = useState(mockLeads);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showResults, setShowResults] = useState(true);

  const handleSearch = () => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const filtered = mockLeads.filter(lead =>
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm) ||
      lead.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setSearchResults(filtered);
    setShowResults(true);
    setSelectedLead(null);
  };

  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
    setShowResults(false);
  };

  const handleCloseDetail = () => {
    setSelectedLead(null);
    setShowResults(true);
  };

  const handleSearchInput = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value.trim() === '') {
      setSearchResults([]);
      setShowResults(false);
      setSelectedLead(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-gray-900 to-black p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <CrownIcon size={32} className="text-yellow-500" />
            <h1 className="text-2xl font-bold text-white">William Walker</h1>
          </div>
        </div>

        {/* Logo Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gray-700 rounded-lg p-8 border-2 border-dashed border-gray-600">
              <div className="text-center">
                <UserIcon className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                <p className="text-gray-400 text-sm">WALKTHADUCKING.COM</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-6">
          <div className="flex space-x-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search leads..."
                value={searchTerm}
                onChange={handleSearchInput}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              className="bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-700 hover:to-yellow-800 text-black font-semibold px-6 py-3 rounded-lg transition-all duration-200"
            >
              Search
            </button>
            {(showResults || selectedLead) && (
              <button
                onClick={() => {
                  setShowResults(false);
                  setSelectedLead(null);
                  setSearchTerm('');
                  setSearchResults([]);
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center space-x-2"
              >
                <XMarkIcon className="h-5 w-5" />
                <span>Cancel</span>
              </button>
            )}
          </div>
        </div>

        {/* Search Results */}
        {showResults && searchResults.length > 0 && (
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4">
              Search Results ({searchResults.length} found)
            </h2>
            <div className="space-y-3">
              {searchResults.map((lead) => (
                <LeadSearchResult
                  key={lead.id}
                  lead={lead}
                  onClick={handleLeadSelect}
                />
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {showResults && searchResults.length === 0 && searchTerm.trim() !== '' && (
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-6">
            <div className="text-center">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Results Found</h3>
              <p className="text-gray-400">
                No leads found matching "{searchTerm}". Try a different search term.
              </p>
            </div>
          </div>
        )}

        {/* Lead Detail View */}
        {selectedLead && (
          <LeadDetailView lead={selectedLead} onClose={handleCloseDetail} />
        )}

        {/* Instructions */}
        {!showResults && !selectedLead && (
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-3">How to Use Manual CRM</h3>
            <ul className="text-gray-300 space-y-2">
              <li>• Enter a name, location, phone number, or email in the search field</li>
              <li>• Click "Search" to find matching leads in the database</li>
              <li>• Click on any search result to view detailed lead information</li>
              <li>• Use the action buttons to call, email, or add notes to leads</li>
              <li>• Click "Cancel" to clear results and start a new search</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const ManualCRMSystem = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-900">
        <MainContent>
          <ManualCRMComponent />
        </MainContent>
      
    </div>
  );
};

export default ManualCRMSystem;